// src/pages/ClientForm.jsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateDMY } from '../utils/dateUtils';
import { getCompanyConfigCached } from '../utils/invoiceUtils';
import DocumentTile, { DOCUMENT_GRID } from '../components/UI/DocumentTile';
import SignatureField from '../components/UI/SignatureField';
import emailjs from 'emailjs-com'; // npm install emailjs-com
import jsPDF from 'jspdf'; // npm install jspdf
import autoTable from 'jspdf-autotable'; // npm install jspdf-autotable
import { toCurrency } from '../utils/adminMetrics';

// The documents a new client is onboarded with.
const CLIENT_FILES = [
  { name: 'identificationFile', type: 'Identification', required: 'Identification Document is required' },
  { name: 'payslipFile', type: 'Payslip', label: 'Latest payslip', required: 'Latest Payslip is required' },
  { name: 'bankStatementFile', type: 'Bank Statement', hint: 'The last three months', required: 'Three Months Bank Statement is required' },
  { name: 'whatsAppPhotoFile', type: 'Client Photo', accept: 'image/*', required: 'Client Photo is required' },
  { name: 'proofOfResidenceFile', type: 'Proof Of Residence', hint: 'Municipality bill or rental contract' },
];

const ClientForm = () => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
  // The documents, picked on the shared tiles; each is kept as [file], as a
  // file input would give it.
  useEffect(() => {
    CLIENT_FILES.forEach((f) => register(f.name, f.required ? { required: f.required } : {}));
  }, [register]);
  const [step, setStep] = useState(1);
  const REQ_EMPTY = 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20';
  const REQ_FILLED = '';
  const reqCls = (name) => `w-full px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border ${String(watch(name) || '').trim() ? REQ_FILLED : REQ_EMPTY} rounded-lg text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 transition-colors`;
  // The walk-in client's signature (an image), from the shared signature field.
  const [walkInSignature, setWalkInSignature] = useState(null);
  const [isWalkIn, setIsWalkIn] = useState(false);
  const [contracts, setContracts] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const [companyConfig, setCompanyConfig] = useState(null);
  // AI document-correspondence prompt — set when the server returns a 409
  // (DOCUMENT_CORRESPONDENCE_BLOCKED) or 503 (CHECK_UNAVAILABLE). Holds the
  // last submitted form payload so the user can retry with forceSubmit=true.
  const [correspondencePrompt, setCorrespondencePrompt] = useState(null);
  const [isForceSubmitting, setIsForceSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        setCompanyConfig(cfg || null);
      } catch {
        setCompanyConfig(null);
      }
    })();
  }, []);

  const buildClientFormData = (data, { forceSubmit = false } = {}) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key.includes('File')) {
        const file = data[key]?.[0];
        if (file) formData.append(key, file);
      } else {
        formData.append(key, data[key]);
      }
    });
    if (forceSubmit) formData.append('forceSubmit', 'true');
    return formData;
  };

  const submitClient = async (data, { forceSubmit = false } = {}) => {
    const formData = buildClientFormData(data, { forceSubmit });
    return axios.post(apiUrl('/api/clients'), formData, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
    });
  };

  const onSubmit = async (data) => {
    setSubmissionError(null);
    try {
      await submitClient(data);
      alert('Client onboarded successfully');
      await generateContracts(data);
    } catch (error) {
      const code = error?.response?.data?.code;
      // AI document-correspondence checks: open the in-page prompt so the user
      // can review and (if they have force_submit) override.
      if (code === 'DOCUMENT_CORRESPONDENCE_BLOCKED' || code === 'DOCUMENT_CORRESPONDENCE_CHECK_UNAVAILABLE') {
        const payload = error.response.data;
        setCorrespondencePrompt({
          code,
          summary: payload.error || 'Document verification flagged issues.',
          canForceSubmit: !!payload.canForceSubmit,
          report: payload.correspondenceReport || null,
          details: payload.details || null,
          data,
        });
        return;
      }
      const limitReachedMessage = code === 'CLIENT_LIMIT_REACHED' ? 'Reached client limit' : null;
      const apiMessage =
        limitReachedMessage ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Error onboarding client';
      setSubmissionError(apiMessage);
      await generateContracts(data); // Generate contracts even on failure
      alert(apiMessage);
    }
  };

  const proceedWithForceSubmit = async () => {
    if (!correspondencePrompt?.data) return;
    if (!correspondencePrompt.canForceSubmit) return;
    setIsForceSubmitting(true);
    try {
      await submitClient(correspondencePrompt.data, { forceSubmit: true });
      alert('Client onboarded (document check overridden via Force Submit).');
      await generateContracts(correspondencePrompt.data);
      setSubmissionError(null);
      setCorrespondencePrompt(null);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Force submit failed.';
      alert(msg);
      setSubmissionError(msg);
    } finally {
      setIsForceSubmitting(false);
    }
  };

  const generateContracts = async (data) => {
    // Dates typed as YYYY-MM-DD are printed as dd/mm/yyyy.
    const typedDate = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(String(v || '').trim()) ? formatDateDMY(String(v).trim()) : (v || ''));
    const signatureImg = isWalkIn ? walkInSignature : null;
    const lenderName = String(companyConfig?.company_name || '').trim() || '______________________________';

    const contractsData = {
      loanAgreement: `
        LOAN AGREEMENT

        Preamble
        This loan agreement only applies to loans not exceeding a period of 5 months.
        Entered into between:
        ${lenderName} ("the Lender") and ${data.fullName} ("the Borrower")
      `,
      clientConsent: `
        CLIENT INFORMATION CONSENT AGREEMENT
        I, ${data.fullName}, residing at ${data.residentialAddress || '______________________________'}, hereby grant consent to ${lenderName}, as follows:
        1. I authorize ${lenderName} to collect, process, and store my personal information...
        [Detailed terms to be inserted here]
      `,
      debitOrder: `
        DEBIT ORDER CONSENT AGREEMENT
        I, ${data.fullName}, residing at ${data.residentialAddress || '______________________________'}, hereby grant consent to ${lenderName}, as follows:
        1. I authorize ${lenderName} to deduct N$10,000 monthly via debit order from my account on the 1st of each month for 3 months, starting from ${formatDateDMY(new Date())}.
      `,
    };

    if (isWalkIn) {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4', // A4 size: 210mm x 297mm
      });
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text('LOAN AGREEMENT', 20, 20);
      doc.setFont("helvetica", "normal");

      // Preamble and Parties
      doc.text(contractsData.loanAgreement.split('\n').map(line => line.trim()).join('\n'), 20, 30, { maxWidth: 170 });

      // Borrower’s Personal Information Table
      let lastY = 50;
      autoTable(doc, {
        startY: lastY,
        head: [['Field', 'Details']],
        body: [
          ['Postal address', `${data.postalAddress}   Tel no: ${data.postalTelNo || ''}`],
          ['Residential address', data.residentialAddress],
          ['Marital Status', data.maritalStatus],
          ['I.D./Passport No', data.idPassportNo],
          ['Occupation', data.occupation],
          ['Employer Tel no', data.employerPhone],
          ['Employer Name', data.employerName],
          ['Employer Address', data.employerAddress],
          ['Payslip/Employee No', data.payslipNumber],
          ['Bank', data.bankName],
          ['Branch', data.branchCode],
          ['Bank Account No', data.accountNumber],
          ['Type of Account', data.accountType],
          ['Reference 1', `Name: ${data.ref1Name || ''}   Tel No: ${data.ref1TelNo || ''}`],
          ['Reference 2', `Name: ${data.ref2Name || ''}   Tel No: ${data.ref2TelNo || ''}`],
        ],
        theme: 'grid',
        styles: { cellPadding: 2, fontSize: 10, halign: 'left' },
        columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 120 } },
      });
      lastY = doc.autoTable.previous.finalY || lastY; // Update lastY with the finalY from the table

      // Loan Amount Table
      autoTable(doc, {
        startY: lastY + 10,
        head: [['Item', 'Details']],
        body: [
          ['Loan Amount (Paid to Borrower)', toCurrency(data.loanAmount)],
          ['Finance Charges', `__% (Fixed/Variable, max 30%)   N$${data.financeCharges || ''}`],
          ['Total Repayable', toCurrency(data.totalRepayable)],
          ['Installment Amount', toCurrency(data.installmentAmount)],
          ['First Installment Due Date', typedDate(data.firstInstallmentDate)],
          ['Last Installment Due Date', typedDate(data.lastInstallmentDate)],
          ['Number of Installments', data.numberOfInstallments || ''],
          ['Frequency', data.frequency || ''],
          ['Penalty Interest', `__% per month (max 5%, 3 months)`],
          ['Period of Loan', `End Date: ${typedDate(data.loanPeriodEndDate)}`],
        ],
        theme: 'grid',
        styles: { cellPadding: 2, fontSize: 10, halign: 'left' },
        columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 120 } },
      });
      lastY = doc.autoTable.previous.finalY || lastY; // Update lastY with the finalY from the table

      // Acknowledgment and Signatures
      doc.text('I acknowledge that this agreement has been completed in full prior to my signature.', 20, lastY + 10);
      const signatureY = lastY + 20;
      doc.text('Signed at ' + (data.signedAt || '') + ' on this ____ day of ' + (data.signedDay || '') + ' 20' + (data.signedYear || '') + '.', 20, signatureY);
      doc.text('Witness:', 20, signatureY + 10);
      doc.line(30, signatureY + 10, 60, signatureY + 10); // Witness line
      doc.text('(Signature of Borrower)', 70, signatureY + 10);
      doc.line(130, signatureY + 10, 180, signatureY + 10); // Borrower signature line
      if (signatureImg) {
        doc.addImage(signatureImg, 'PNG', 130, signatureY + 5, 50, 20); // Align borrower signature above line
      }
      doc.text('Signed at ' + (data.signedAtLender || '') + ' on this ____ day of ' + (data.signedDayLender || '') + ' 20' + (data.signedYearLender || '') + '.', 20, signatureY + 20);
      doc.text('Witness:', 20, signatureY + 30);
      doc.line(30, signatureY + 30, 60, signatureY + 30); // Witness line
      doc.text('(Signature of Lender)', 70, signatureY + 30);
      doc.line(130, signatureY + 30, 180, signatureY + 30); // Lender signature line
      if (signatureImg) {
        doc.addImage(signatureImg, 'PNG', 130, signatureY + 25, 50, 20); // Align lender signature above line
      }

      // Terms & Conditions (simplified text due to space; adjust as needed)
      doc.addPage();
      doc.setFont("helvetica", "bold");
      doc.text('TERMS & CONDITIONS', 20, 20);
      doc.setFont("helvetica", "normal");
      const termsText = `
        1. Confidentiality: The microlender may not disclose confidential information without consent, except to a registered credit bureau or by court order...
        2. Legal Costs: Limited to party and party scale per Magistrates’ Courts Act, 1944 or High Court Act, 1990...
        3. Consent to Judgment: Void if obtained before default...
        4. Dispute Resolution: Refer to NAMFISA with Annexure A...
        5. Cooling Off: Cancel within 3 business days with pro rata repayment...
        6. Prepayment: No penalties for early settlement...
        7. The Whole Contract: Changes must be in writing and signed...
        8. Governing Law: Laws of Namibia...
        9. Disclosure: Compliance with Microlending Act, 2018 requirements...
      `.split('\n').map(line => line.trim()).join('\n');
      doc.text(termsText, 20, 30, { maxWidth: 170 });

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setContracts({ ...contractsData, pdfUrl });
    } else {
      emailjs.send('service_your_id', 'template_your_id', {
        to_email: data.email,
        loan_agreement: contractsData.loanAgreement,
        client_consent: contractsData.clientConsent,
        debit_order: contractsData.debitOrder,
      }, 'user_your_id')
        .then(() => console.log('Contracts sent'))
        .catch(err => console.error('Email error:', err));
      setContracts(contractsData);
    }
  };

  const nextStep = async () => {
    // On Step 1, validate existing client before allowing next step
    if (step === 1) {
      const email = (watch('email') || '').trim();
      const idPassportNo = (watch('idPassportNo') || '').trim();

      if (email || idPassportNo) {
        setIsCheckingExisting(true);
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            apiUrl('/api/clients/check-exists'),
            { email, idPassportNo },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSubmissionError(null);
          setStep(step + 1);
        } catch (err) {
          const apiMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Client already exists.';
          setSubmissionError(apiMessage);
        } finally {
          setIsCheckingExisting(false);
        }
        return;
      }
    }

    setStep(step + 1);
  };
  const prevStep = () => setStep(step - 1);

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-4">Client Onboarding</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {step === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">Step 1: Personal Information</h2>
            <input {...register('fullName', { required: 'Full Name is required' })} placeholder="Full Name" className={reqCls('fullName')} />
            {errors.fullName && <p className="text-rose-500 text-xs mt-1">{errors.fullName.message}</p>}
            <input {...register('idPassportNo', { required: 'ID/Passport No is required' })} placeholder="ID/Passport No" className={reqCls('idPassportNo')} />
            {errors.idPassportNo && <p className="text-rose-500 text-xs mt-1">{errors.idPassportNo.message}</p>}
            <input {...register('postalAddress', { required: 'Postal Address is required' })} placeholder="Postal Address" className={reqCls('postalAddress')} />
            {errors.postalAddress && <p className="text-rose-500 text-xs mt-1">{errors.postalAddress.message}</p>}
            <input {...register('postalTelNo', { required: 'Postal Tel No is required' })} placeholder="Postal Tel No" className={reqCls('postalTelNo')} />
            {errors.postalTelNo && <p className="text-rose-500 text-xs mt-1">{errors.postalTelNo.message}</p>}
            <input {...register('residentialAddress', { required: 'Residential Address is required' })} placeholder="Residential Address" className={reqCls('residentialAddress')} />
            {errors.residentialAddress && <p className="text-rose-500 text-xs mt-1">{errors.residentialAddress.message}</p>}
            <input {...register('maritalStatus', { required: 'Marital Status is required' })} placeholder="Marital Status" className={reqCls('maritalStatus')} />
            {errors.maritalStatus && <p className="text-rose-500 text-xs mt-1">{errors.maritalStatus.message}</p>}
            <input {...register('email', { required: 'Email is required' })} placeholder="Email" className={reqCls('email')} />
            {errors.email && <p className="text-rose-500 text-xs mt-1">{errors.email.message}</p>}
            <button type="button" onClick={nextStep} disabled={isCheckingExisting} className="bg-blue-500 text-white p-2 rounded w-full disabled:opacity-60">
              {isCheckingExisting ? 'Checking...' : 'Next'}
            </button>
          </div>
        )}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">Step 2: Employment Information</h2>
            <input {...register('occupation', { required: 'Occupation is required' })} placeholder="Occupation" className={reqCls('occupation')} />
            {errors.occupation && <p className="text-rose-500 text-xs mt-1">{errors.occupation.message}</p>}
            <input {...register('employerName', { required: 'Employer Name is required' })} placeholder="Employer Name" className={reqCls('employerName')} />
            {errors.employerName && <p className="text-rose-500 text-xs mt-1">{errors.employerName.message}</p>}
            <input {...register('employerPhone', { required: 'Employer Phone is required' })} placeholder="Employer Phone" className={reqCls('employerPhone')} />
            {errors.employerPhone && <p className="text-rose-500 text-xs mt-1">{errors.employerPhone.message}</p>}
            <input {...register('employerAddress', { required: 'Employer Address is required' })} placeholder="Employer Address" className={reqCls('employerAddress')} />
            {errors.employerAddress && <p className="text-rose-500 text-xs mt-1">{errors.employerAddress.message}</p>}
            <input {...register('payslipNumber', { required: 'Payslip Number is required' })} placeholder="Payslip Number" className={reqCls('payslipNumber')} />
            {errors.payslipNumber && <p className="text-rose-500 text-xs mt-1">{errors.payslipNumber.message}</p>}
            <button type="button" onClick={prevStep} className="bg-gray-500 text-white p-2 rounded w-full mb-2">Previous</button>
            <button type="button" onClick={nextStep} className="bg-blue-500 text-white p-2 rounded w-full">Next</button>
          </div>
        )}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">Step 3: Banking and Loan Details</h2>
            <input {...register('bankName', { required: 'Bank Name is required' })} placeholder="Bank Name" className={reqCls('bankName')} />
            {errors.bankName && <p className="text-rose-500 text-xs mt-1">{errors.bankName.message}</p>}
            <input {...register('branchCode', { required: 'Branch Code is required' })} placeholder="Branch Code" className={reqCls('branchCode')} />
            {errors.branchCode && <p className="text-rose-500 text-xs mt-1">{errors.branchCode.message}</p>}
            <input {...register('accountNumber', { required: 'Account Number is required' })} placeholder="Account Number" className={reqCls('accountNumber')} />
            {errors.accountNumber && <p className="text-rose-500 text-xs mt-1">{errors.accountNumber.message}</p>}
            <select {...register('accountType', { required: 'Account Type is required' })} className={reqCls('accountType')}>
              <option value="">Select Account Type</option>
              <option value="Cheque">Cheque</option>
              <option value="Savings">Savings</option>
              <option value="Transmission">Transmission</option>
            </select>
            {errors.accountType && <p className="text-rose-500 text-xs mt-1">{errors.accountType.message}</p>}
            <input {...register('loanAmount', { required: 'Loan Amount is required' })} placeholder="Loan Amount (N$)" className={reqCls('loanAmount')} />
            {errors.loanAmount && <p className="text-rose-500 text-xs mt-1">{errors.loanAmount.message}</p>}
            <input {...register('financeCharges', { required: 'Finance Charges (N$)' })} placeholder="Finance Charges (N$)" className={reqCls('financeCharges')} />
            {errors.financeCharges && <p className="text-rose-500 text-xs mt-1">{errors.financeCharges.message}</p>}
            <input {...register('totalRepayable', { required: 'Total Repayable (N$)' })} placeholder="Total Repayable (N$)" className={reqCls('totalRepayable')} />
            {errors.totalRepayable && <p className="text-rose-500 text-xs mt-1">{errors.totalRepayable.message}</p>}
            <input {...register('installmentAmount', { required: 'Installment Amount (N$)' })} placeholder="Installment Amount (N$)" className={reqCls('installmentAmount')} />
            {errors.installmentAmount && <p className="text-rose-500 text-xs mt-1">{errors.installmentAmount.message}</p>}
            <input {...register('firstInstallmentDate', { required: 'First Installment Due Date' })} placeholder="First Installment Due Date (e.g., 2025-08-01)" className={reqCls('firstInstallmentDate')} />
            {errors.firstInstallmentDate && <p className="text-rose-500 text-xs mt-1">{errors.firstInstallmentDate.message}</p>}
            <input {...register('lastInstallmentDate', { required: 'Last Installment Due Date' })} placeholder="Last Installment Due Date (e.g., 2025-10-01)" className={reqCls('lastInstallmentDate')} />
            {errors.lastInstallmentDate && <p className="text-rose-500 text-xs mt-1">{errors.lastInstallmentDate.message}</p>}
            <input {...register('numberOfInstallments', { required: 'Number of Installments' })} placeholder="Number of Installments" className={reqCls('numberOfInstallments')} />
            {errors.numberOfInstallments && <p className="text-rose-500 text-xs mt-1">{errors.numberOfInstallments.message}</p>}
            <input {...register('frequency', { required: 'Frequency' })} placeholder="Frequency (e.g., monthly)" className={reqCls('frequency')} />
            {errors.frequency && <p className="text-rose-500 text-xs mt-1">{errors.frequency.message}</p>}
            <input {...register('loanPeriodEndDate', { required: 'Loan Period End Date' })} placeholder="Loan Period End Date (e.g., 2025-10-01)" className={reqCls('loanPeriodEndDate')} />
            {errors.loanPeriodEndDate && <p className="text-rose-500 text-xs mt-1">{errors.loanPeriodEndDate.message}</p>}
            <button type="button" onClick={prevStep} className="bg-gray-500 text-white p-2 rounded w-full mb-2">Previous</button>
            <button type="button" onClick={nextStep} className="bg-blue-500 text-white p-2 rounded w-full">Next</button>
          </div>
        )}
        {step === 4 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-3">Step 4: References and Signatures</h2>
            <input {...register('ref1Name', { required: 'Reference 1 Name is required' })} placeholder="Reference 1 Name" className={reqCls('ref1Name')} />
            {errors.ref1Name && <p className="text-rose-500 text-xs mt-1">{errors.ref1Name.message}</p>}
            <input {...register('ref1TelNo', { required: 'Reference 1 Tel No is required' })} placeholder="Reference 1 Tel No" className={reqCls('ref1TelNo')} />
            {errors.ref1TelNo && <p className="text-rose-500 text-xs mt-1">{errors.ref1TelNo.message}</p>}
            <input {...register('ref2Name', { required: 'Reference 2 Name is required' })} placeholder="Reference 2 Name" className={reqCls('ref2Name')} />
            {errors.ref2Name && <p className="text-rose-500 text-xs mt-1">{errors.ref2Name.message}</p>}
            <input {...register('ref2TelNo', { required: 'Reference 2 Tel No is required' })} placeholder="Reference 2 Tel No" className={reqCls('ref2TelNo')} />
            {errors.ref2TelNo && <p className="text-rose-500 text-xs mt-1">{errors.ref2TelNo.message}</p>}
            <input {...register('signedAt', { required: 'Signed At is required' })} placeholder="Signed At" className={reqCls('signedAt')} />
            {errors.signedAt && <p className="text-rose-500 text-xs mt-1">{errors.signedAt.message}</p>}
            <input {...register('signedDay', { required: 'Signed Day is required' })} placeholder="Signed Day (e.g., 15)" className={reqCls('signedDay')} />
            {errors.signedDay && <p className="text-rose-500 text-xs mt-1">{errors.signedDay.message}</p>}
            <input {...register('signedYear', { required: 'Signed Year is required' })} placeholder="Signed Year (e.g., 25)" className={reqCls('signedYear')} />
            {errors.signedYear && <p className="text-rose-500 text-xs mt-1">{errors.signedYear.message}</p>}
            <input {...register('signedAtLender', { required: 'Signed At (Lender) is required' })} placeholder="Signed At (Lender)" className={reqCls('signedAtLender')} />
            {errors.signedAtLender && <p className="text-rose-500 text-xs mt-1">{errors.signedAtLender.message}</p>}
            <input {...register('signedDayLender', { required: 'Signed Day (Lender) is required' })} placeholder="Signed Day (Lender) (e.g., 15)" className={reqCls('signedDayLender')} />
            {errors.signedDayLender && <p className="text-rose-500 text-xs mt-1">{errors.signedDayLender.message}</p>}
            <input {...register('signedYearLender', { required: 'Signed Year (Lender) is required' })} placeholder="Signed Year (Lender) (e.g., 25)" className={reqCls('signedYearLender')} />
            {errors.signedYearLender && <p className="text-rose-500 text-xs mt-1">{errors.signedYearLender.message}</p>}
            <div className={DOCUMENT_GRID}>
              {CLIENT_FILES.map((f) => (
                <div key={f.name}>
                  <DocumentTile
                    type={f.type}
                    label={f.label}
                    hint={f.hint}
                    accept={f.accept}
                    file={watch(f.name)?.[0] || null}
                    required={Boolean(f.required)}
                    onPick={(file) => setValue(f.name, [file], { shouldValidate: true })}
                    onRemove={() => setValue(f.name, null, { shouldValidate: true })}
                  />
                  {errors[f.name] && <p className="text-rose-500 text-xs mt-1">{errors[f.name].message}</p>}
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" checked={isWalkIn} onChange={(e) => setIsWalkIn(e.target.checked)} className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" /> Walk-in Client
            </label>
            {isWalkIn && (
              <div>
                <SignatureField label="Client's signature" value={walkInSignature} onChange={setWalkInSignature} required />
                {!walkInSignature && <p className="text-rose-500 text-xs mt-1 text-center">Please sign and press Save Signature.</p>}
              </div>
            )}
            <button type="button" onClick={prevStep} className="bg-gray-500 text-white p-2 rounded w-full mb-2">Previous</button>
            <button type="submit" className="bg-blue-500 text-white p-2 rounded w-full">Submit</button>
          </div>
        )}
        {submissionError && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-300">
            <h3 className="font-bold">{step < 4 ? 'Cannot Continue' : 'Submission Failed'}</h3>
            <p>{submissionError}</p>
            {contracts && (
              <div className="mt-2">
                <h4 className="font-semibold">Generated Contracts:</h4>
                <pre className="bg-gray-100 dark:bg-slate-900/40 text-gray-800 dark:text-slate-200 rounded p-2 overflow-auto">{Object.entries(contracts).map(([key, value]) => `${key}:\n${value}\n\n`).join('')}</pre>
                {contracts.pdfUrl && (
                  <a href={contracts.pdfUrl} download="signed_contracts.pdf" className="bg-green-500 text-white hover:text-white dark:text-white dark:hover:text-white p-2 rounded mt-2 inline-block">Download PDF</a>
                )}
              </div>
            )}
          </div>
        )}
        {contracts && !submissionError && (
          <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/40 text-green-700 dark:text-green-300">
            <h3 className="font-bold">Generated Contracts:</h3>
            <pre className="bg-gray-100 dark:bg-slate-900/40 text-gray-800 dark:text-slate-200 rounded p-2 overflow-auto">{Object.entries(contracts).map(([key, value]) => `${key}:\n${value}\n\n`).join('')}</pre>
            {contracts.pdfUrl && (
              <a href={contracts.pdfUrl} download="signed_contracts.pdf" className="bg-green-500 text-white hover:text-white dark:text-white dark:hover:text-white p-2 rounded mt-2 inline-block">Download PDF</a>
            )}
          </div>
        )}
      </form>

      {correspondencePrompt && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700/40 flex items-center justify-between">
              <h3 className="text-base font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                <span>⚠️</span>
                {correspondencePrompt.code === 'DOCUMENT_CORRESPONDENCE_CHECK_UNAVAILABLE'
                  ? 'Document verification unavailable'
                  : 'Document verification flagged issues'}
              </h3>
              <button
                type="button"
                onClick={() => setCorrespondencePrompt(null)}
                className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 text-xl leading-none p-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto">
              <p className="text-sm text-gray-700 dark:text-slate-200">
                {correspondencePrompt.summary}
              </p>
              {correspondencePrompt.details && (
                <p className="text-xs text-gray-500 dark:text-slate-400 italic">{correspondencePrompt.details}</p>
              )}

              {correspondencePrompt.report?.mismatchDocuments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-1">Mismatches</h4>
                  <ul className="text-sm text-gray-700 dark:text-slate-300 list-disc list-inside space-y-1">
                    {correspondencePrompt.report.mismatchDocuments.map((d, i) => (
                      <li key={`mm-${i}`}>
                        <span className="font-semibold">{d.label || d.name}:</span>{' '}
                        {(d.mismatchedFields || []).map((f) => f.label || f.field).filter(Boolean).join(', ') || d.status || 'mismatch'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {correspondencePrompt.report?.reviewDocuments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-1">Needs review</h4>
                  <ul className="text-sm text-gray-700 dark:text-slate-300 list-disc list-inside space-y-1">
                    {correspondencePrompt.report.reviewDocuments.map((d, i) => (
                      <li key={`rv-${i}`}>
                        <span className="font-semibold">{d.label || d.name}:</span> {d.status}
                        {d.notes?.length ? ` — ${d.notes.join('; ')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {correspondencePrompt.report?.crossDocumentConflicts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-1">Conflicts between documents</h4>
                  <ul className="text-sm text-gray-700 dark:text-slate-300 list-disc list-inside space-y-1">
                    {correspondencePrompt.report.crossDocumentConflicts.map((c, i) => (
                      <li key={`cx-${i}`}>{c.summary || c.message || JSON.stringify(c)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={`mt-3 p-3 rounded-lg text-xs ${correspondencePrompt.canForceSubmit ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700/40' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700/40'}`}>
                {correspondencePrompt.canForceSubmit
                  ? 'You have permission to override and add this client anyway. Click "Proceed anyway" to continue.'
                  : 'You do not have permission to override this check. Please correct the documents or contact an administrator with the force_submit permission.'}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCorrespondencePrompt(null)}
                disabled={isForceSubmitting}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              {correspondencePrompt.canForceSubmit && (
                <button
                  type="button"
                  onClick={proceedWithForceSubmit}
                  disabled={isForceSubmitting}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {isForceSubmitting ? 'Submitting…' : 'Proceed anyway'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ClientForm;